import {
  Box,
  Button,
  Divider,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import { useSearch } from '@tanstack/react-router';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Query } from 'src/components/atoms/query';
import { PageHeader } from 'src/components/molecules/page-header';
import Page from 'src/components/organisms/page/page';
import { Pagination } from 'src/components/organisms/pagination';
import { useMeilisearchWithUrlParams } from 'src/hooks/meilisearch/use-meilisearch-with-url-params';
import { useDialog } from 'src/hooks/use-dialog';
import { useSortController } from 'src/hooks/use-sort-controller';
import { Plus } from 'src/icons/plus';
import { Can, withPermissions } from 'src/permissions/can';
import { useUserStore } from 'src/store/user-store';
import { SortOrder } from 'src/types/enum/sort-order';
import { useUpdateEmployeesGroupDefault } from '../../api/hooks/use-update-employees-group-default';
import { fetchEmployeesGroups } from '../../api/lib/employees-groups';
import { employeesGroupsKeys } from '../../api/query-keys/employees-groups';
import EmployeesGroupActionsTableMenu from '../molecules/employees-group-actions-table-menu';
import { CreateEmployeesGroupDialog } from '../organisms/dialogs/create-employees-group-dialog/create-employees-group-dialog';
import { EmployeesGroupsTable } from '../organisms/employees-groups-table';
import { EmployeesGroupActionsSlotPayload } from '../organisms/employees-groups-table-row';

const EmployeesGroups = () => {
  const { query, perPage, page } = useSearch({
    from: '/_layout/tenant/employees/_layout/groups',
    select(state) {
      return {
        page: state.page,
        perPage: state.limit,
        query: state.q,
        filter: state.filter,
      };
    },
    structuralSharing: true,
  });

  const { t } = useTranslation(['employeesGroups', 'common']);

  const { directoryToken } = useUserStore();

  const [sortController, setSortController] = useSortController({
    sortBy: 'name',
    sortOrder: SortOrder.DESC,
  });

  const defaultSearchStateConfig = useMemo(() => {
    return {
      sort: [`${sortController.sortBy}:${sortController.sortOrder}`],
    };
  }, [sortController.sortBy, sortController.sortOrder]);

  const {
    results,
    handleChangeQuery,
    handleChangePage,
    isLoading,
    isFetching,
    handleChangeLimit,
    error,
    handleChangeSort,
  } = useMeilisearchWithUrlParams(
    fetchEmployeesGroups,
    employeesGroupsKeys.list(directoryToken ?? '', page, perPage),
    defaultSearchStateConfig,
  );

  const totalPages = Math.ceil((results?.estimatedTotalHits ?? 0) / perPage);

  // Create employees group
  const [
    isOpenCreateEmployeesGroupDialog,
    handleOpenCreateEmployeesGroupDialog,
    handleCloseCreateEmployeesGroupDialog,
  ] = useDialog();

  const { mutate: updateEmployeesGroupDefault } =
    useUpdateEmployeesGroupDefault();

  const actionsSlot = useCallback(
    ({ token, isDefault }: EmployeesGroupActionsSlotPayload) => (
      <EmployeesGroupActionsTableMenu
        isDefault={isDefault}
        onRemoveFromDefault={() => {
          updateEmployeesGroupDefault({
            employeeGroupToken: token,
            isDefault: false,
          });
        }}
        onSetAsDefault={() => {
          updateEmployeesGroupDefault({
            employeeGroupToken: token,
            isDefault: true,
          });
        }}
      />
    ),
    [],
  );

  return (
    <>
      <PageHeader
        title={t('employeesGroups:common.title')}
        extras={
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {!isLoading ? (
              <Typography variant="body2">
                {t('employeesGroups:common.totalAmount', {
                  amount: results?.estimatedTotalHits ?? 0,
                })}
              </Typography>
            ) : (
              <Skeleton height={42} width={200} />
            )}

            <Can I="create" a="EmployeesGroups" passThrough>
              {allowed => (
                <Tooltip
                  title={t('common:notSufficientPermissions')}
                  disableHoverListener={allowed}
                  disableFocusListener={allowed}>
                  <span>
                    <Button
                      sx={{ ml: 2 }}
                      onClick={handleOpenCreateEmployeesGroupDialog}
                      color="primary"
                      disabled={!allowed}
                      startIcon={<Plus fontSize="small" />}
                      variant="contained"
                      id="add_group_button"
                      size="large">
                      {t('employeesGroups:common.addNewGroup')}
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Can>
          </Box>
        }
      />

      <Page.ContentContainer>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            px: 3,
            py: 2.5,
          }}>
          <Query
            searchAsType
            disabled={false}
            onChange={handleChangeQuery}
            sx={{
              minWidth: 200,
            }}
            value={query}
          />
        </Box>

        <EmployeesGroupsTable
          data={results?.hits ?? []}
          isLoading={isLoading}
          error={error?.response?.data?.message}
          sortController={sortController}
          setSortController={setSortController}
          handleChangeSort={handleChangeSort}
          actionsSlot={actionsSlot}
        />

        <Divider sx={{ mt: 'auto' }} />

        <Pagination
          disabled={isLoading}
          isFetching={isFetching}
          changePage={handleChangePage}
          rowsCount={results?.estimatedTotalHits || 0}
          page={page}
          perPage={perPage}
          totalPages={totalPages}
          handlePerPageChange={handleChangeLimit}
        />
      </Page.ContentContainer>

      <CreateEmployeesGroupDialog
        open={isOpenCreateEmployeesGroupDialog}
        onCancel={handleCloseCreateEmployeesGroupDialog}
      />
    </>
  );
};

export default withPermissions(
  permissions => permissions.cannot('read', 'EmployeesGroups'),
  EmployeesGroups,
);
